const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * Multi-order purchases (e.g. SUP quantity > 1) share one persisted booking
 * number — the per-board A/B/… letters are display-only and must be derived
 * the same way on every surface (end-user app tickets, per-board confirmation
 * emails, admin bookings lists, booking details): siblings grouped by booking
 * number, sorted by their order id, letter assigned by index. Groups of one
 * keep the bare number and get no entry in the returned map.
 *
 * Returns a map of row id → display booking number (bare number + letter).
 * Callers fall back to the raw booking number for ids without an entry.
 */
export const buildBookingNumberLetterMap = <T>(
    rows: T[],
    accessors: {
        getId: (row: T) => string;
        getBookingNumber: (row: T) => string;
        /**
         * Group key defaults to the booking number. The cross-tenant list
         * passes a tenant-prefixed key so equal numbers from different
         * tenants never form one group.
         */
        getGroupKey?: (row: T) => string;
    },
): Map<string, string> => {
    const { getId, getBookingNumber, getGroupKey } = accessors;

    const groups = new Map<string, T[]>();
    rows.forEach((row) => {
        const bookingNumber = getBookingNumber(row);
        if (!bookingNumber) return;
        const key = getGroupKey ? getGroupKey(row) : bookingNumber;
        const group = groups.get(key);
        if (group) {
            group.push(row);
        } else {
            groups.set(key, [row]);
        }
    });

    const displayNumberById = new Map<string, string>();
    groups.forEach((group) => {
        if (group.length <= 1) return;
        [...group]
            .sort((a, b) => getId(a).localeCompare(getId(b)))
            .forEach((row, index) => {
                displayNumberById.set(
                    getId(row),
                    `${getBookingNumber(row)}${ALPHABET[index] ?? ''}`,
                );
            });
    });
    return displayNumberById;
};
